import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  buildSeriesByMetric,
  OTHER_OCCUPATION_LABEL,
  XAxisGranularity,
  TimeSeriesMetric,
} from './utils/timeSeriesProcessing';

import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { Timeline } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DashboardData } from '../../types';
import {
  COLORS,
  moveLabelToEnd,
  NON_COMPLIANCE_RESULT,
  UNFILLED_REASON_LABEL,
  generateContentColors,
} from './utils/dataProcessing';
import type { DashboardFilters as DashboardFilterState } from '../../hooks/useDashboardData';
import { createTooltipItemSorter } from './utils/chartUtils';
import { getTimeSeriesCaptureStateKey } from './utils/timeSeriesImageTargets';

interface TimeSeriesTabProps {
  data: DashboardData;
  hasReasonData: boolean;
  // DashboardFiltersはダッシュボード全体共通の条件を表す．
  // 時系列専用の追加条件はこの型に混在させず，TimeSeriesTab側で別途管理する．
  filters: Pick<DashboardFilterState, 'departments' | 'occupations'>;
}

export interface TimeSeriesTabHandle {
  setGranularity: (granularity: XAxisGranularity) => void;
  setMetric: (metric: YAxisMetric) => void;
  setDisplayMode: (mode: DisplayMode) => void;
  getCurrentState: () => {
    granularity: XAxisGranularity;
    metric: YAxisMetric;
    displayMode: DisplayMode;
  };
}

const X_AXIS_GRANULARITY_OPTIONS: { value: XAxisGranularity; label: string }[] = [
  { value: 'month', label: '月次' },
  { value: 'quarter', label: '四半期' },
  { value: 'year', label: '年次' },
  { value: 'fiscalYear', label: '年度' },
];

const Y_AXIS_METRIC_OPTIONS = [
  '結果の内訳',
  '未実施理由の内訳',
  '未実施理由の職種別内訳',
] as const;

type YAxisMetric = typeof Y_AXIS_METRIC_OPTIONS[number];
type DisplayMode = 'ratio' | 'count';

const PERCENT_ROUNDING_DIGITS = 1;
const HUNDRED_PERCENT_EPSILON = 1e-6;
const IN_BAR_LABEL_MIN_PERCENT = 5;
const IN_BAR_LABEL_MIN_WIDTH = 40;
const IN_BAR_LABEL_MIN_HEIGHT = 20;

const formatPercentage = (value: number, fractionDigits = PERCENT_ROUNDING_DIGITS) => {
  const numericValue = Number(value);
  const normalizedValue =
    Math.abs(numericValue - 100) < HUNDRED_PERCENT_EPSILON ? 100 : numericValue;

  if (normalizedValue === 100) {
    return '100%';
  }

  return `${normalizedValue.toFixed(fractionDigits)}%`;
};

const metricToSeriesMetric: Record<YAxisMetric, TimeSeriesMetric> = {
  '結果の内訳': 'resultBreakdown',
  '未実施理由の内訳': 'nonComplianceReasonBreakdown',
  '未実施理由の職種別内訳': 'nonComplianceOccupationBreakdown',
};

const TimeSeriesTab = forwardRef<TimeSeriesTabHandle, TimeSeriesTabProps>(
  ({ data, hasReasonData, filters }, ref) => {
  const [xAxisGranularity, setXAxisGranularity] = useState<XAxisGranularity>('month');
  const [yAxisMetric, setYAxisMetric] = useState<YAxisMetric>('結果の内訳');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('ratio');

  useEffect(() => {
    if (!hasReasonData && yAxisMetric === '未実施理由の内訳') {
      setYAxisMetric('結果の内訳');
    }
  }, [hasReasonData, yAxisMetric]);

  const { series: seriesData, seriesKeys, insufficientRange } = useMemo(
    () => buildSeriesByMetric(metricToSeriesMetric[yAxisMetric], xAxisGranularity, data, {
      allowShortRange: true,
    }),
    [data, xAxisGranularity, yAxisMetric]
  );

  const contentColorMap = useMemo(() => generateContentColors(data), [data]);
  const chartData = useMemo(
    () => seriesData.filter(bucket => bucket.total > 0),
    [seriesData]
  );

  const reasonColorMap = useMemo(
    () =>
      seriesKeys.reduce<Record<string, string>>((acc, key, index) => {
        if (key === UNFILLED_REASON_LABEL) {
          acc[key] = '#9e9e9e';
          return acc;
        }
        acc[key] = COLORS[index % COLORS.length];
        return acc;
      }, {}),
    [seriesKeys]
  );

  const occupationColorMap = useMemo(
    () =>
      seriesKeys.reduce<Record<string, string>>((acc, key, index) => {
        if (key === OTHER_OCCUPATION_LABEL) {
          acc[key] = '#b0bec5';
          return acc;
        }
        acc[key] = COLORS[(index + 8) % COLORS.length];
        return acc;
      }, {}),
    [seriesKeys]
  );

  const activeColorMap =
    yAxisMetric === '結果の内訳'
      ? contentColorMap
      : yAxisMetric === '未実施理由の内訳'
        ? reasonColorMap
        : occupationColorMap;

  const legendKeys = useMemo(
    () => moveLabelToEnd(seriesKeys, NON_COMPLIANCE_RESULT),
    [seriesKeys]
  );

  const tooltipItemSorter = useMemo(
    () => createTooltipItemSorter(legendKeys),
    [legendKeys]
  );

  const metricTitle =
    yAxisMetric === '結果の内訳'
      ? '結果の内訳'
      : yAxisMetric === '未実施理由の内訳'
        ? '未実施理由の内訳'
        : '未実施理由の職種別内訳';

  useImperativeHandle(
    ref,
    () => ({
      setGranularity: (granularity: XAxisGranularity) => setXAxisGranularity(granularity),
      setMetric: (metric: YAxisMetric) => setYAxisMetric(metric),
      setDisplayMode: (mode: DisplayMode) => setDisplayMode(mode),
      getCurrentState: () => ({
        granularity: xAxisGranularity,
        metric: yAxisMetric,
        displayMode,
      }),
    }),
    [displayMode, xAxisGranularity, yAxisMetric]
  );

  const handleGranularityChange = (event: SelectChangeEvent<XAxisGranularity>) => {
    setXAxisGranularity(event.target.value as XAxisGranularity);
  };

  const handleMetricChange = (event: SelectChangeEvent<YAxisMetric>) => {
    setYAxisMetric(event.target.value as YAxisMetric);
  };

  const handleDisplayModeChange = (event: SelectChangeEvent<DisplayMode>) => {
    setDisplayMode(event.target.value as DisplayMode);
  };

  const yAxisDomain = displayMode === 'ratio' ? [0, 100] : ['auto', 'auto'];
  const yAxisTickFormatter = (value: number) =>
    displayMode === 'ratio' ? formatPercentage(Number(value)) : `${Number(value)}件`;

  return (
    <Stack spacing={3} className="timeseries-tab-root">
      {insufficientRange && (
        <Alert severity="info">データ期間が2ヶ月未満のため、参考値として表示しています。</Alert>
      )}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <Timeline sx={{ mr: 1 }} /> 時系列分析
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          対象: 部署{filters.departments.length > 0 ? `${filters.departments.length}件` : '全件'}、職種
          {filters.occupations.length > 0 ? `${filters.occupations.length}件` : '全件'}（未選択時は全件）
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="timeseries-x-axis-granularity-label">横軸粒度</InputLabel>
            <Select
              labelId="timeseries-x-axis-granularity-label"
              value={xAxisGranularity}
              label="横軸粒度"
              onChange={handleGranularityChange}
            >
              {X_AXIS_GRANULARITY_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="timeseries-y-axis-metric-label">縦軸指標</InputLabel>
            <Select
              labelId="timeseries-y-axis-metric-label"
              value={yAxisMetric}
              label="縦軸指標"
              onChange={handleMetricChange}
            >
              {Y_AXIS_METRIC_OPTIONS.map(option => {
                if (option === '未実施理由の内訳' && !hasReasonData) {
                  return null;
                }

                return (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="timeseries-display-mode-label">表示モード</InputLabel>
            <Select
              labelId="timeseries-display-mode-label"
              value={displayMode}
              label="表示モード"
              onChange={handleDisplayModeChange}
            >
              <MenuItem value="ratio">割合</MenuItem>
              <MenuItem value="count">件数</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ p: 0 }}>
        <Box
          sx={{ pt: 3, px: 3, pb: 2, backgroundColor: 'background.paper' }}
          className="timeseries-chart-capture-target"
          data-testid="timeseries-chart-capture-target"
          data-dashboard-capture-state={getTimeSeriesCaptureStateKey(
            yAxisMetric,
            xAxisGranularity,
            displayMode,
          )}
        >
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="h6">{metricTitle}</Typography>
          </Stack>
          <Box sx={{ height: 380, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 12, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis domain={yAxisDomain} tickFormatter={(value) => yAxisTickFormatter(Number(value))} />
                <Tooltip
                  itemSorter={tooltipItemSorter}
                  formatter={(value, name, props) => {
                    const itemName = typeof name === 'string' ? name : String(name);
                    const count = Number(props.payload?.[`${itemName}Count`] || 0);
                    const ratio = Number(props.payload?.[itemName] || 0);

                    if (displayMode === 'count') {
                      return [`${count}件`, itemName];
                    }

                    return [`${formatPercentage(ratio)} (${count}件)`, itemName];
                  }}
                />
                <Legend
                  content={() => (
                    <ul
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        flexWrap: 'wrap',
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                      }}
                    >
                      {legendKeys.map((seriesKey, index) => (
                        <li
                          key={seriesKey}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              backgroundColor: activeColorMap[seriesKey] || COLORS[index % COLORS.length],
                              display: 'inline-block',
                            }}
                          />
                          <span>{seriesKey}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                />
                {legendKeys.map((seriesKey, index) => (
                  <Bar
                    key={seriesKey}
                    dataKey={displayMode === 'ratio' ? seriesKey : `${seriesKey}Count`}
                    name={seriesKey}
                    stackId="ratio"
                    fill={activeColorMap[seriesKey] || COLORS[index % COLORS.length]}
                    isAnimationActive={false}
                    label={(props) => {
                      const { x, y, width, height, index: barIndex } = props;

                      if (
                        typeof x !== 'number' ||
                        typeof y !== 'number' ||
                        typeof width !== 'number' ||
                        typeof height !== 'number' ||
                        typeof barIndex !== 'number' ||
                        width < IN_BAR_LABEL_MIN_WIDTH ||
                        height < IN_BAR_LABEL_MIN_HEIGHT
                      ) {
                        return <g />;
                      }

                      const dataPoint = chartData[barIndex];
                      if (!dataPoint) {
                        return <g />;
                      }

                      const ratio = Number(dataPoint[seriesKey] || 0);
                      if (ratio < IN_BAR_LABEL_MIN_PERCENT) {
                        return <g />;
                      }

                      const labelText =
                        displayMode === 'ratio'
                          ? formatPercentage(ratio)
                          : `${Number(dataPoint[`${seriesKey}Count`] || 0)}件`;

                      return (
                        <text
                          x={x + width / 2}
                          y={y + height / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#fff"
                          fontSize={12}
                          fontWeight="bold"
                        >
                          {labelText}
                        </text>
                      );
                    }}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box sx={{ px: 3, pb: 3, backgroundColor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>
            バケット別件数
          </Typography>
          <List dense disablePadding>
            {chartData.map(bucket => (
              <ListItem key={String(bucket.label)} disableGutters>
                <ListItemText
                  primary={`${bucket.label}: 合計 ${bucket.total}件`}
                  secondary={legendKeys
                    .map(seriesKey => `${seriesKey} ${bucket[`${seriesKey}Count`]}件`)
                    .join(' / ')}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>
    </Stack>
  );
});

export default TimeSeriesTab;
